import { useState, useMemo } from "react";
import { CalendarIcon, ChevronDown } from "lucide-react";
import { format, addDays, addMonths } from "date-fns";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const serviceOptions = [
  { name: "Exterior Detail", price: 75 },
  { name: "Interior Detail", price: 110 },
  { name: "Full Detail", price: 180 },
];

const additionOptions = [
  { name: "Leather Conditioning", price: 10 },
  { name: "Fabric Protectant", price: 12 },
  { name: "Quick Shine Sealant", price: 15 },
];

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    vehicle: "",
    service_type: "",
    preferred_date: "",
    message: "",
  });
  
  const [selectedAdditions, setSelectedAdditions] = useState([]);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);

  // Updated Date Range Logic: 3 Days to 1 Month
  const minDate = addDays(new Date(), 3); 
  const maxDate = addMonths(new Date(), 1); 

  const WEB3FORMS_ACCESS_KEY = "c687f3f7-36d4-4abd-ae87-4f973edc65d6"; 

  const totalPrice = useMemo(() => {
    const sPrice = serviceOptions.find(s => s.name === form.service_type)?.price || 0;
    const aPrice = selectedAdditions.reduce((sum, name) => {
      const opt = additionOptions.find(a => a.name === name);
      return sum + (opt ? opt.price : 0);
    }, 0);
    return sPrice + aPrice;
  }, [form.service_type, selectedAdditions]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const toggleAddition = (name) => {
    setSelectedAdditions(prev => 
      prev.includes(name) ? prev.filter(a => a !== name) : [...prev, name]
    );
  };

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);
    if (selectedDate) {
      setForm({ ...form, preferred_date: format(selectedDate, "yyyy-MM-dd") });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanPhone = form.phone.replace(/\D/g, "");
    
    if (!form.name || !form.service_type || !form.message || !date) {
      toast.error("Please fill in all required fields, including a valid date.");
      return;
    }

    setLoading(true);

    const formData = {
      ...form,
      phone: cleanPhone,
      additions: selectedAdditions.length > 0 ? selectedAdditions.join(", ") : "None",
      total_estimate: `$${totalPrice}`,
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: `New Booking: ${form.service_type} from ${form.name}`,
    };

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      if (result.success) {
        toast.success(`Success! Estimated Total: $${totalPrice}`);
        setForm({ name: "", email: "", phone: "", vehicle: "", service_type: "", preferred_date: "", message: "" });
        setSelectedAdditions([]);
        setDate(null);
      }
    } catch (err) {
      toast.error("Network error.");
    } finally {
      setLoading(false);
    }
  };

  const inputStyles = "bg-[#141414] border-[#262626] text-white placeholder:text-[#555] focus:border-white focus-visible:ring-0 focus-visible:ring-offset-0 rounded-sm h-11";

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 text-white">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          
          <div className="md:col-span-5">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#737373] mb-4">Get In Touch</p>
            <h2 className="text-3xl sm:text-4xl font-bold uppercase text-white mb-6">Book Your Detail</h2>
            <div className="space-y-4 text-sm text-[#A3A3A3]">
              <p><span className="text-white font-semibold">Location:</span> Sammamish, WA</p>
              <p><span className="text-white font-semibold">Email:</span> sammamishautodetail@gmail.com</p>
            </div>
          </div>

          <div className="md:col-span-7">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Input name="name" value={form.name} onChange={handleChange} placeholder="Name *" className={inputStyles} />
                <Input name="email" type="email" value={form.email} onChange={handleChange} placeholder="Email *" className={inputStyles} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Input name="phone" value={form.phone} onChange={handleChange} placeholder="Phone (10 Digits) *" className={inputStyles} />
                <Input name="vehicle" value={form.vehicle} onChange={handleChange} placeholder="Vehicle Make/Model" className={inputStyles} />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase font-bold text-[#737373]">Service *</Label>
                <Select value={form.service_type} onValueChange={(val) => setForm({ ...form, service_type: val })}>
                  <SelectTrigger className={inputStyles}>
                    <SelectValue placeholder="Select Service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626] text-white">
                    {serviceOptions.map((s) => (
                      <SelectItem key={s.name} value={s.name}>{s.name} (${s.price})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase font-bold text-[#737373]">Additions (Optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={`${inputStyles} w-full justify-between font-normal`}>
                      <span className={selectedAdditions.length === 0 ? "text-[#555]" : "text-white"}>
                        {selectedAdditions.length === 0 
                          ? "Select additions" 
                          : `${selectedAdditions.length} selected`}
                      </span>
                      <ChevronDown className="h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-2 bg-[#141414] border-[#262626]">
                    <div className="space-y-1">
                      {additionOptions.map((a) => (
                        <div 
                          key={a.name}
                          onClick={() => toggleAddition(a.name)}
                          className="flex items-center space-x-3 p-2 rounded cursor-pointer hover:bg-[#1a1a1a] transition-colors"
                        >
                          <input 
                            type="checkbox" 
                            readOnly 
                            checked={selectedAdditions.includes(a.name)}
                            className="w-4 h-4 rounded border-[#262626] bg-transparent accent-white"
                          />
                          <div className="flex-1 flex justify-between items-center text-sm">
                            <span className="text-white">{a.name}</span>
                            <span className="text-[#737373] text-xs">+${a.price}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase font-bold text-[#737373]">Preferred Date (3 Days - 1 Month out)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={`${inputStyles} w-full justify-start`}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#141414] border-[#262626]">
                    <Calendar 
                      mode="single" 
                      selected={date} 
                      onSelect={handleDateSelect} 
                      disabled={(d) => d < minDate || d > maxDate} 
                      className="text-white bg-[#141414]" 
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <Textarea name="message" value={form.message} onChange={handleChange} placeholder="Message *" rows={4} className={inputStyles} />

              {/* COMPACT ESTIMATE DISPLAY */}
              <div className="flex justify-between items-center px-4 py-2 border border-dashed border-[#262626] rounded-sm">
                <span className="text-[10px] uppercase font-bold text-[#737373]">Est. Total</span>
                <span className="text-lg font-bold text-white">${totalPrice}</span>
              </div>

              <Button 
                type="submit" 
                disabled={loading} 
                className="w-full bg-transparent border border-[#262626] text-[#A3A3A3] font-bold uppercase py-6 hover:bg-white hover:text-black transition-all duration-300"
              >
                {loading ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
